// @flow
import styled from '@emotion/styled';
import { SPACING, typography } from '@tablekit/theme';
import {
  DESKTOP_BREAKPOINT,
  LAPTOP_BREAKPOINT,
  MOBILE_BREAKPOINT
} from 'utils/theme';
import columnWidth from 'utils/columnWidth';
import Title from 'styled/Title';
import RichText from 'components/RichText';
import CTAButton from 'components/CTAButton';
import Link from 'components/Link';
import ContentWithImage, { isRightCheck } from 'components/ContentWithImage';
import type {
  BlokComponentPropsType,
  ParagraphWithImageType
} from 'components/types';

const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
  max-width: ${columnWidth(12)}%;

  @media ${LAPTOP_BREAKPOINT} {
    // central 5 / 6 columns
    width: ${columnWidth(10)}%;
    align-self: ${isRightCheck('flex-end', 'flex-start')};
  }

  @media ${DESKTOP_BREAKPOINT} {
    width: ${columnWidth(8)}%;
    align-self: center;
  }
`;

const Body = styled(RichText)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  ${typography('L3')};

  @media ${MOBILE_BREAKPOINT} {
    align-items: flex-start;
    ${(props) => typography(props.isPostBody ? 'L4' : 'L3')(props)};
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  @media ${MOBILE_BREAKPOINT} {
    justify-content: flex-start;
  }
  margin: ${SPACING.L6} -${SPACING.L3} 0;

  & > * {
    margin: 0 ${SPACING.L3} !important;
  }
`;

const ParagraphWithImage = ({
  blok,
  isRight = true,
  isPostBody = false,
  isBackgroundDisabled = false
}: BlokComponentPropsType<ParagraphWithImageType> & {
  isBackgroundDisabled?: boolean,
  isPostBody?: boolean,
  isRight?: boolean
}) => {
  const {
    action_primary_link: primaryLink,
    action_primary_text: primaryText,
    action_secondary_link: secondaryLink,
    action_secondary_text: secondaryText,
    body,
    image,
    title,
    hide_parallax: hideParallax
  } = blok;
  const buttons = [];
  if (primaryLink && primaryText) {
    buttons.push(
      <CTAButton
        component={Link}
        key="primary"
        link={primaryLink}
        gaReference={`Paragraph Primary Link; ${title}`}
      >
        {primaryText}
      </CTAButton>
    );
  }
  if (secondaryLink && secondaryText) {
    buttons.push(
      <CTAButton
        key="secondary"
        component={Link}
        appearance="subtle"
        link={secondaryLink}
        gaReference={`Paragraph Secondary Link; ${title}`}
      >
        {secondaryText}
      </CTAButton>
    );
  }
  return (
    <ContentWithImage
      image={image}
      imageAlt={title}
      hideParallax={hideParallax}
      isRight={isRight}
      isBackgroundDisabled={isBackgroundDisabled}
    >
      <TextColumn isRight={isRight}>
        <Title align="left">{title}</Title>
        <Body isPostBody={isPostBody} richText={body} />
        {!!buttons.length && <ActionsContainer>{buttons}</ActionsContainer>}
      </TextColumn>
    </ContentWithImage>
  );
};

export default ParagraphWithImage;
